/**
 * Consolidated-invoice presentation shared by the invoice page, the credit
 * dialog and the browser print (#3933). Amounts stay integer cents.
 */

export interface ConsolidatedRow {
  bookingId: string;
  workOrderNumber: string | null;
  registrationNumber: string | null;
  /** YYYY-MM-DD, or null when the work order has no completion date. */
  completedDate: string | null;
  customerReference: string | null;
  taxPercent: number | null;
  netCents: number;
  taxCents: number;
  grossCents: number;
}

export interface ConsolidatedWorkOrderLink {
  bookingId: string;
  workOrderNumber: string | null;
  registrationNumber: string | null;
  customerReference: string | null;
  released: boolean;
}

export interface InvoiceConsolidation {
  periodStart: string | null;
  periodEnd: string | null;
  rows: ConsolidatedRow[];
  workOrders: ConsolidatedWorkOrderLink[];
}

/** Frozen onto contentSnapshot when a consolidated invoice is finalized. */
export interface ConsolidatedSnapshotSection {
  periodStart: string | null;
  periodEnd: string | null;
  rows: ConsolidatedRow[];
}

export interface WorkOrderSummaryRow {
  bookingId: string;
  workOrderNumber: string | null;
  registrationNumber: string | null;
  completedDate: string | null;
  customerReference: string | null;
  grossCents: number;
}

export interface VatSummaryRow {
  taxPercent: number | null;
  netCents: number;
  taxCents: number;
  grossCents: number;
}

const REFERENCE_MAX = 140;

export function isCollectingInvoice(status: string): boolean {
  return status === 'collecting';
}

export function isConsolidationReferenceEditable(status: string): boolean {
  return status === 'collecting' || status === 'draft';
}

/** Empty and whitespace become null. Longer than 140 characters is rejected. */
export function normalizeCustomerReference(value: string | null): string | null | undefined {
  if (value === null) return null;
  const trimmed = value.trim();
  if (trimmed === '') return null;
  if (trimmed.length > REFERENCE_MAX) return undefined;
  return trimmed;
}

export function readLineBookingId(line: object): string | null {
  if (!('bookingId' in line)) return null;
  const value = line.bookingId;
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function isConsolidatedRow(value: unknown): value is ConsolidatedRow {
  if (typeof value !== 'object' || value === null) return false;
  if (!('bookingId' in value) || typeof value.bookingId !== 'string') return false;
  if (!('netCents' in value) || typeof value.netCents !== 'number') return false;
  if (!('taxCents' in value) || typeof value.taxCents !== 'number') return false;
  if (!('grossCents' in value) || typeof value.grossCents !== 'number') return false;
  return true;
}

function nullableString(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  return typeof value === 'string' ? value : null;
}

/** Snapshot section written at finalize. Missing or malformed data is ignored. */
export function readConsolidationSnapshot(invoice: object): ConsolidatedSnapshotSection | null {
  if (!('contentSnapshot' in invoice)) return null;
  const snapshot = invoice.contentSnapshot;
  if (typeof snapshot !== 'object' || snapshot === null || !('consolidation' in snapshot)) return null;
  const section = snapshot.consolidation;
  if (typeof section !== 'object' || section === null || !('rows' in section)) return null;
  const rows = section.rows;
  if (!Array.isArray(rows) || !rows.every(isConsolidatedRow)) return null;
  return {
    periodStart: 'periodStart' in section ? nullableString(section.periodStart) : null,
    periodEnd: 'periodEnd' in section ? nullableString(section.periodEnd) : null,
    rows,
  };
}

function workOrdersFromRows(rows: readonly ConsolidatedRow[]): ConsolidatedWorkOrderLink[] {
  const seen = new Set<string>();
  const links: ConsolidatedWorkOrderLink[] = [];
  for (const row of rows) {
    if (row.bookingId === '' || seen.has(row.bookingId)) continue;
    seen.add(row.bookingId);
    links.push({
      bookingId: row.bookingId,
      workOrderNumber: row.workOrderNumber,
      registrationNumber: row.registrationNumber,
      customerReference: row.customerReference,
      released: false,
    });
  }
  return links;
}

/**
 * Finalized invoices prefer the frozen snapshot. Collecting and draft invoices
 * use the live rows so a reference edit shows up immediately.
 */
export function resolveConsolidationView(input: {
  status: string;
  live: InvoiceConsolidation | null;
  snapshot: ConsolidatedSnapshotSection | null;
}): InvoiceConsolidation | null {
  const frozen = input.status !== 'draft' && !isCollectingInvoice(input.status);
  if (frozen && input.snapshot) {
    return {
      periodStart: input.snapshot.periodStart,
      periodEnd: input.snapshot.periodEnd,
      rows: input.snapshot.rows,
      workOrders: input.live?.workOrders ?? workOrdersFromRows(input.snapshot.rows),
    };
  }
  if (input.live) return input.live;
  if (!input.snapshot) return null;
  return {
    periodStart: input.snapshot.periodStart,
    periodEnd: input.snapshot.periodEnd,
    rows: input.snapshot.rows,
    workOrders: workOrdersFromRows(input.snapshot.rows),
  };
}

/** One main-page row per work order. VAT splits stay in the VAT summary. */
export function summarizeWorkOrders(rows: readonly ConsolidatedRow[]): WorkOrderSummaryRow[] {
  const order: string[] = [];
  const byBooking = new Map<string, WorkOrderSummaryRow>();
  for (const row of rows) {
    const existing = byBooking.get(row.bookingId);
    if (!existing) {
      order.push(row.bookingId);
      byBooking.set(row.bookingId, {
        bookingId: row.bookingId,
        workOrderNumber: row.workOrderNumber,
        registrationNumber: row.registrationNumber,
        completedDate: row.completedDate,
        customerReference: row.customerReference,
        grossCents: row.grossCents,
      });
      continue;
    }
    existing.grossCents += row.grossCents;
    if (existing.customerReference === null && row.customerReference !== null) {
      existing.customerReference = row.customerReference;
    }
    if (existing.completedDate === null && row.completedDate !== null) {
      existing.completedDate = row.completedDate;
    }
    if (existing.workOrderNumber === null && row.workOrderNumber !== null) {
      existing.workOrderNumber = row.workOrderNumber;
    }
    if (existing.registrationNumber === null && row.registrationNumber !== null) {
      existing.registrationNumber = row.registrationNumber;
    }
  }
  return order.flatMap((bookingId) => {
    const row = byBooking.get(bookingId);
    return row ? [row] : [];
  });
}

export function summarizeVat(rows: readonly ConsolidatedRow[]): VatSummaryRow[] {
  const order: Array<number | null> = [];
  const byRate = new Map<string, VatSummaryRow>();
  for (const row of rows) {
    const key = row.taxPercent === null ? 'none' : String(row.taxPercent);
    const existing = byRate.get(key);
    if (!existing) {
      order.push(row.taxPercent);
      byRate.set(key, {
        taxPercent: row.taxPercent,
        netCents: row.netCents,
        taxCents: row.taxCents,
        grossCents: row.grossCents,
      });
      continue;
    }
    existing.netCents += row.netCents;
    existing.taxCents += row.taxCents;
    existing.grossCents += row.grossCents;
  }
  return order.flatMap((rate) => {
    const row = byRate.get(rate === null ? 'none' : String(rate));
    return row ? [row] : [];
  });
}

export function workOrdersForCredit(
  workOrders: readonly ConsolidatedWorkOrderLink[],
): ConsolidatedWorkOrderLink[] {
  const seen = new Set<string>();
  const result: ConsolidatedWorkOrderLink[] = [];
  for (const workOrder of workOrders) {
    if (workOrder.bookingId === '' || seen.has(workOrder.bookingId)) continue;
    seen.add(workOrder.bookingId);
    result.push(workOrder);
  }
  return result;
}
